import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../../api';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { toast } from 'sonner';
import { FileText, Download, Printer } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { formatearFecha } from '../../i18n/fechas';

const NormativaModal = ({ isOpen, onClose, user }) => {
  const { t } = useTranslation('auth');
  const [loading, setLoading] = useState(true);
  const [normativaContent, setNormativaContent] = useState(null);

  useEffect(() => {
    if (isOpen) {
      fetchNormativa();
    }
  }, [isOpen]);

  const fetchNormativa = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/client/normativa`);
      setNormativaContent(response.data);
    } catch (error) {
      console.error('Error fetching normativa:', error);
      toast.error(t('normativa.errorCargar'));
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = () => {
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <html>
        <head>
          <title>Normativa - Método Pilar Domínguez</title>
          <style>
            @page {
              size: A4;
              margin: 15mm 20mm;
            }
            @media print {
              html, body {
                width: 210mm;
                height: 297mm;
              }
            }
            body { font-family: Arial, sans-serif; padding: 40px; max-width: 800px; margin: 0 auto; }
            h1 { color: #0d6b58; border-bottom: 2px solid #0d6b58; padding-bottom: 10px; }
            h2 { color: #333; margin-top: 30px; }
            p { line-height: 1.6; color: #444; }
            ul { margin: 10px 0; }
            li { margin: 8px 0; }
            .footer { margin-top: 40px; padding-top: 20px; border-top: 1px solid #ddd; font-size: 12px; color: #666; }
          </style>
        </head>
        <body>
          ${normativaContent?.html || `<h1>Normativa</h1><p>${t('normativa.cargando')}</p>`}
          <div class="footer">
            <p>${t('normativa.generadoEl', { fecha: formatearFecha(new Date()) })}</p>
            <p>${t('normativa.tagline')}</p>
          </div>
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.print();
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader className="flex-shrink-0">
          <DialogTitle className="flex items-center gap-2 text-teal-700">
            <FileText className="w-5 h-5" />
            {t('normativa.titulo')}
          </DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto px-1">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600"></div>
            </div>
          ) : normativaContent ? (
            <div className="space-y-4">
              {/* Contenido de la normativa */}
              <div 
                className="prose prose-sm max-w-none"
                dangerouslySetInnerHTML={{ __html: normativaContent.html }}
              />
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">
              <FileText className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>{t('normativa.noSePudo')}</p>
            </div>
          )}
        </div>

        <div className="flex-shrink-0 flex justify-end gap-2 pt-4 border-t">
          <Button
            variant="outline"
            onClick={handlePrint}
            disabled={!normativaContent}
            className="flex items-center gap-2"
          >
            <Printer className="w-4 h-4" />
            {t('normativa.imprimir')}
          </Button>
          <Button onClick={onClose} className="bg-teal-600 hover:bg-teal-700">
            {t('normativa.cerrar')}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default NormativaModal;
